import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    Timeline as ActivityIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    TrendingUp as UsageIcon,
    Warning as UnusedIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import ActivityList from './activities/ActivityList';
import { activityService } from '../../services/activityService';

function ActivityManagement() {
    const [stats, setStats] = useState(null);
    const { showSuccess, showError } = useApiHandler();

    useEffect(() => {
        loadActivityStats();
    }, []);

    const loadActivityStats = async () => {
        try {
            const response = await activityService.getActivityStats();
            
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            showError('Error loading activity statistics');
        }
    };

    const refreshStats = () => {
        loadActivityStats();
    };

    return (
        <DashboardLayout>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Activity Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage tracking activities used in shipment tracking system
                </Typography>
            </Box>

            {/* Statistics Cards */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <ActivityIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Total Activities</Typography>
                                </Box>
                                <Typography variant="h4" color="primary" gutterBottom>
                                    {stats.total}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label={`${stats.active} Active`} 
                                        color="success" 
                                        size="small" 
                                    />
                                    <Chip 
                                        label={`${stats.inactive} Inactive`} 
                                        color="default" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <ActiveIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Active Activities</Typography>
                                </Box>
                                <Typography variant="h4" color="success.main" gutterBottom>
                                    {stats.active}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label="Ready to Use" 
                                        color="success" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <UsageIcon color="info" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Total Usage</Typography>
                                </Box>
                                <Typography variant="h4" color="info.main" gutterBottom>
                                    {stats.total_tracking_entries}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label="Tracking Entries" 
                                        color="info" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <UnusedIcon color="warning" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Unused Activities</Typography>
                                </Box>
                                <Typography variant="h4" color="warning.main" gutterBottom>
                                    {stats.unused}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label="Never Used" 
                                        color="warning" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Most Used Activities */}
            {stats && stats.most_used && stats.most_used.length > 0 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Most Used Activities
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {stats.most_used.map((activity, index) => (
                                        <Chip
                                            key={activity.id}
                                            label={`${activity.name} (${activity.usage_count})`}
                                            color={index === 0 ? 'primary' : 'secondary'}
                                            variant={index < 3 ? 'filled' : 'outlined'}
                                        />
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Activity Management */}
            <Paper sx={{ width: '100%' }}>
                <ActivityList onDataChange={refreshStats} />
            </Paper>
        </DashboardLayout>
    );
}

export default ActivityManagement; 